import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { queryKeys } from "../config";
import { UMSrequestClient } from "../stores/requestClient";
import { accountQueryKey } from "./useAccount";
import type { AccountResponseData } from "./useAccountTypes";

export const TOTPEnrollResponseSchema = z.object({
  totpDeviceID: z.string(),
  verificationImage: z.string(),
  key: z.string(),
  recoveryCode: z.string(),
});

export type TOTPEnrollResponse = z.infer<typeof TOTPEnrollResponseSchema>;

export const TOTPVerifyRequestSchema = z.object({
  totpDeviceID: z.string(),
  code: z.string().length(6),
});

export type TOTPVerifyRequest = z.infer<typeof TOTPVerifyRequestSchema>;

export enum TOTPErrorCode {
  InvalidCode = "INVALID_TOTP_CODE",
  AlreadyEnabled = "TOTP_ALREADY_ENABLED",
  DeviceNotFound = "TOTP_DEVICE_NOT_FOUND",
  DeviceIdMismatch = "TOTP_DEVICE_ID_MISMATCH",
}

export function useEnrollTOTP({ enabled = false }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...queryKeys.authenticatedQueryKey, "totp-enroll"],
    queryFn: async () => {
      const response = await UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: "/2fa/totp",
      });

      return response.json<TOTPEnrollResponse>();
    },
    enabled,
  });
}

export function useVerifyTOTP({ onError }: { onError?: (error: Error) => void } = {}) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, TOTPVerifyRequest>({
    mutationKey: [...queryKeys.authenticatedQueryKey, "totp-verify"],
    mutationFn: async (data: TOTPVerifyRequest) => {
      await UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: "/2fa/totp/verify",
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.setQueryData<AccountResponseData | undefined>(accountQueryKey, (oldData) => {
        if (!oldData) return undefined;
        return {
          ...oldData,
          twoFactorTotpEnabled: true,
        } satisfies AccountResponseData;
      });
    },
    onError,
  });
}
